import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Dumbbell, Flame, Calendar, ShieldCheck, Sparkles, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const GOAL_COPY = {
  lose_fat:     { tagline: "Cut fat, keep your strength",   sessions: 4, calBias: -0.18, proteinG: 2.0, focus: "Lean" },
  build_muscle: { tagline: "Hypertrophy-built, AI-tuned",   sessions: 5, calBias: 0.10,  proteinG: 1.9, focus: "Grow" },
  get_stronger: { tagline: "Compound-led strength plan",    sessions: 4, calBias: 0.05,  proteinG: 1.9, focus: "Power" },
  mobility:     { tagline: "Move freely, recover faster",   sessions: 5, calBias: 0.0,   proteinG: 1.6, focus: "Mobile" },
  endurance:    { tagline: "Aerobic engine, daily fuel",    sessions: 5, calBias: -0.05, proteinG: 1.6, focus: "Engine" },
  maintain:     { tagline: "Sustainable, balanced training", sessions: 4, calBias: 0.0,  proteinG: 1.8, focus: "Balanced" },
};

const baseTDEE = 2400; // generic estimate; gets personalised properly post-PlanForm

export default function PlanPreview({ data, onContinue }) {
  const [matchCount, setMatchCount] = useState(null);
  const goal = data?.goal || "maintain";
  const meta = GOAL_COPY[goal] || GOAL_COPY.maintain;
  const equipment = (data?.equipment && data.equipment.length) ? data.equipment : ["Bodyweight"];
  const injuries = data?.injuries || [];
  const kcal = Math.round(baseTDEE * (1 + meta.calBias));
  const protein = Math.round(80 * meta.proteinG);

  useEffect(() => {
    (async () => {
      try {
        // Pull whole library once then filter client-side for speed
        const all = await api.exercises({ limit: 2000 });
        const eqSet = new Set(equipment);
        const injSet = new Set(injuries);
        const filtered = all.filter((e) => {
          const eqOk = !e.equipment || e.equipment.length === 0 || e.equipment.some((x) => eqSet.has(x));
          const safe = !(e.contraindications || []).some((c) => injSet.has(c));
          return eqOk && safe;
        });
        setMatchCount(filtered.length);
      } catch { setMatchCount(0); }
    })();
  }, [equipment, injuries]);

  const stats = [
    { Icon: Calendar, label: "Sessions / week",     value: meta.sessions, color: "text-lime-400" },
    { Icon: Flame,    label: "Daily target",        value: `${kcal} kcal`,  color: "text-orange-400" },
    { Icon: Dumbbell, label: "Protein / day",       value: `${protein} g`, color: "text-sky-400" },
    {
      Icon: ShieldCheck,
      label: injuries.length ? "Injury-safe moves" : "Exercises matched",
      value: matchCount == null ? "…" : `${matchCount.toLocaleString()}`,
      color: "text-emerald-400",
    },
  ];

  const itemAnim = (i) => ({
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    transition: { delay: 0.1 + i * 0.08, duration: 0.4, ease: "easeOut" },
  });

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#0b0b0c] text-white overflow-y-auto"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="plan-preview-screen"
    >
      <div className="mx-auto max-w-md px-6 pt-6 pb-32">
        <motion.div
          initial={{ scale: 0.6, opacity: 0, rotate: -10 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 14 }}
          className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-lime-400 to-emerald-600 grid place-items-center shadow-lg"
        >
          <Sparkles className="h-7 w-7 text-black" />
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="mt-4 text-2xl font-semibold tracking-tight text-center leading-tight"
        >
          Your plan is ready
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.12 }}
          className="text-center text-zinc-400 mt-1.5 text-sm"
        >
          {meta.tagline}
        </motion.p>

        <div className="mt-6 grid grid-cols-2 gap-2.5">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              {...itemAnim(i)}
              className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4"
              data-testid={`preview-stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <s.Icon className={`h-5 w-5 ${s.color} mb-2`} />
              <div className="text-lg font-semibold leading-tight">
                {s.value === "…" ? <Loader2 className="h-4 w-4 inline animate-spin" /> : s.value}
              </div>
              <div className="text-[11px] text-zinc-400 mt-0.5">{s.label}</div>
            </motion.div>
          ))}
        </div>

        <motion.div
          {...itemAnim(4)}
          className="mt-4 rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4"
        >
          <div className="text-xs text-zinc-400">Built around your equipment</div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {equipment.map((e) => (
              <span key={e} className="text-[11px] px-2 py-1 rounded-full bg-zinc-800 text-zinc-200">{e}</span>
            ))}
          </div>
          {injuries.length > 0 && (
            <>
              <div className="text-xs text-zinc-400 mt-3">Avoiding</div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {injuries.map((i) => (
                  <span key={i} className="text-[11px] px-2 py-1 rounded-full bg-red-500/10 text-red-300 border border-red-500/20">{i}</span>
                ))}
              </div>
            </>
          )}
        </motion.div>

        <motion.div {...itemAnim(5)} className="mt-4 rounded-2xl border border-lime-400/30 bg-lime-400/5 p-4">
          <div className="flex items-start gap-2">
            <Sparkles className="h-4 w-4 text-lime-400 mt-0.5 shrink-0" />
            <div className="text-xs text-zinc-300 leading-relaxed">
              Your AI Coach will adapt this every week using your check-ins, soreness, and lift history.
              Today's first workout is already queued up.
            </div>
          </div>
        </motion.div>

        <motion.button
          {...itemAnim(6)}
          onClick={onContinue}
          data-testid="plan-preview-continue-btn"
          className="mt-6 w-full rounded-full h-12 bg-lime-400 text-black font-semibold flex items-center justify-center gap-2"
        >
          Unlock my plan <Sparkles className="h-4 w-4" />
        </motion.button>
      </div>
    </div>
  );
}
